// M12 — Admin Branding sayfası. PLAN-M12-CLIENT-BRANDING §9 (revize).
// /admin/branding — admin-only (system.admin).
// Sol rail: "Mahir AI Default" + müşteri listesi. Sağ: seçilenin BrandingEditor'u.

import { useCallback, useEffect, useState } from "react";
import type { Client, BrandingConfig, BrandingResponse, ClientsListResponse } from "@mahir/shared";
import { api } from "../../api/client";
import {
  CLIENT_PICKER_COPY,
  fillCopy,
  foldTr,
  pushRecentClient,
  readRecentClients,
  type ClientPickerItem,
} from "../../lib/client-picker";
import { useDebouncedValue } from "../../components/common/FilteredTable";
import { useAuthStore } from "../../stores/auth-store";
import {
  deleteClientLogo,
  deleteDefaultLogo,
  disableClientBranding,
  getClientBranding,
  getDefaultBranding,
  updateClientBranding,
  updateDefaultBranding,
  uploadLogoFlow,
} from "../../api/branding";
import { BrandingEditor } from "../../components/branding/BrandingEditor";

type Selection = { kind: "default" } | { kind: "client"; clientId: string; name: string };

/** Sol raydaki liste tavanı — fazlası aramayla (ClientPicker işi, 2026-10-07; eski 200 kalktı). */
const RAIL_LIMIT = 50;

export function AdminBranding(): JSX.Element {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const [clients, setClients] = useState<Client[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);
  const [selection, setSelection] = useState<Selection>({ kind: "default" });
  const [loadingClients, setLoadingClients] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<ClientPickerItem[]>(() => readRecentClients(userId));

  // Sunucu araması (ad/sektör/ülke ILIKE): boş sorguda ilk 50 + toplam.
  useEffect(() => {
    let aborted = false;
    setLoadingClients(true);
    const params = new URLSearchParams({ limit: String(RAIL_LIMIT) });
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
    api<ClientsListResponse>(`/clients?${params.toString()}`)
      .then((res) => {
        if (aborted) return;
        setClients(res.clients.filter((c) => !c.deletedAt));
        setTotal(res.total);
        setError(null);
      })
      .catch((err: Error) => {
        if (!aborted) setError(err.message);
      })
      .finally(() => {
        if (!aborted) setLoadingClients(false);
      });
    return () => {
      aborted = true;
    };
  }, [debouncedSearch]);

  // Sunucu yanıtı gelene kadar yerel TR katlamalı daraltma (anlık geri bildirim).
  const needle = foldTr(search);
  const filtered = needle ? clients.filter((c) => foldTr(c.name).includes(needle)) : clients;
  const recentIds = new Set(recent.map((r) => r.id));
  const recentVisible = needle ? [] : recent;
  const listed = filtered.filter((c) => !recentIds.has(c.id) || needle);
  const more = needle ? 0 : Math.max(0, total - clients.length);

  function pickClient(c: { id: string; name: string }): void {
    setSelection({ kind: "client", clientId: c.id, name: c.name });
    setRecent(pushRecentClient(userId, { id: c.id, name: c.name }));
  }

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)", maxWidth: 1400 }}>
      <header className="mb-6">
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.72rem",
            letterSpacing: "0.08em",
            color: "var(--color-text-3)",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          ADMİN
        </div>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.8rem",
            color: "var(--color-text)",
          }}
        >
          Branding
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--color-text-2)" }}>
          DOCX kurumsal görünüm: logo + renkler + fontlar + header/footer template'leri. Müşteri
          özel brand atanmamışsa Mahir Default kullanılır.
        </p>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: 24,
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          borderRadius: 12,
          overflow: "hidden",
          minHeight: 600,
        }}
      >
        {/* Sol rail */}
        <nav
          style={{
            padding: 12,
            borderRight: "1px solid var(--color-border)",
            background: "var(--color-bg)",
            overflowY: "auto",
            maxHeight: "calc(100vh - 240px)",
          }}
        >
          {/* Default item */}
          <button
            onClick={() => setSelection({ kind: "default" })}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "10px 12px",
              borderRadius: 6,
              marginBottom: 8,
              background: selection.kind === "default" ? "var(--color-surface)" : "transparent",
              border: "1px solid",
              borderColor: selection.kind === "default" ? "var(--color-primary)" : "transparent",
              color: selection.kind === "default" ? "var(--color-primary)" : "var(--color-text)",
              fontSize: "0.92rem",
              fontWeight: selection.kind === "default" ? 600 : 500,
              cursor: "pointer",
            }}
          >
            ★ Mahir AI Default
            <div
              className="text-xs mt-0.5"
              style={{ color: "var(--color-text-3)", fontWeight: 400 }}
            >
              Fallback brand (override yoksa)
            </div>
          </button>

          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--color-text-3)",
              padding: "8px 12px 4px",
            }}
          >
            Müşteriler
          </div>

          <input
            type="search"
            aria-label={CLIENT_PICKER_COPY.placeholder}
            placeholder={CLIENT_PICKER_COPY.placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 10px",
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              fontSize: "0.85rem",
              marginBottom: 8,
            }}
          />

          {loadingClients && (
            <div className="text-xs px-3" role="status" style={{ color: "var(--color-text-3)" }}>
              Yükleniyor…
            </div>
          )}
          {error && (
            <div className="text-xs px-3" style={{ color: "var(--color-error)" }}>
              {error}
            </div>
          )}
          {recentVisible.length > 0 ? (
            <>
              <RailHeader label={CLIENT_PICKER_COPY.recent} />
              {recentVisible.map((c) => (
                <RailItem
                  key={`r-${c.id}`}
                  name={c.name}
                  active={selection.kind === "client" && selection.clientId === c.id}
                  onClick={() => pickClient(c)}
                />
              ))}
              <RailHeader label={CLIENT_PICKER_COPY.results} />
            </>
          ) : null}
          {listed.map((c) => (
            <RailItem
              key={c.id}
              name={c.name}
              active={selection.kind === "client" && selection.clientId === c.id}
              onClick={() => pickClient(c)}
            />
          ))}
          {more > 0 ? (
            <div className="text-xs px-3 mt-2" style={{ color: "var(--color-text-3)" }}>
              {fillCopy(CLIENT_PICKER_COPY.more, { n: more })}
            </div>
          ) : null}
          {!loadingClients && listed.length === 0 && recentVisible.length === 0 && (
            <div
              className="text-xs px-3 mt-2"
              style={{ color: "var(--color-text-3)", fontStyle: "italic" }}
            >
              Müşteri bulunamadı
            </div>
          )}
        </nav>

        {/* Sağ panel */}
        <main style={{ padding: 24, minWidth: 0 }}>
          {selection.kind === "default" ? (
            <DefaultBrandingPanel />
          ) : (
            <ClientBrandingPanel
              key={selection.clientId}
              clientId={selection.clientId}
              clientName={selection.name}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function RailHeader({ label }: { label: string }): JSX.Element {
  return (
    <div
      style={{
        fontSize: "0.66rem",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "var(--color-text-3)",
        padding: "6px 12px 2px",
        fontWeight: 600,
      }}
    >
      {label}
    </div>
  );
}

function RailItem({
  name,
  active,
  onClick,
}: {
  name: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "8px 12px",
        borderRadius: 6,
        marginBottom: 2,
        background: active ? "var(--color-surface)" : "transparent",
        border: "1px solid",
        borderColor: active ? "var(--color-primary)" : "transparent",
        color: active ? "var(--color-primary)" : "var(--color-text-2)",
        fontSize: "0.88rem",
        fontWeight: active ? 600 : 400,
        cursor: "pointer",
      }}
    >
      {name}
    </button>
  );
}

function DefaultBrandingPanel(): JSX.Element {
  const [data, setData] = useState<BrandingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await getDefaultBranding();
      setData(res);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleSave = async (config: BrandingConfig): Promise<void> => {
    await updateDefaultBranding(config);
    await reload();
  };
  const handleLogoUpload = async (file: File): Promise<void> => {
    await uploadLogoFlow({ type: "default" }, file);
    await reload();
  };
  const handleLogoDelete = async (): Promise<void> => {
    await deleteDefaultLogo();
    await reload();
  };

  return (
    <div>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", marginBottom: 8 }}>
        Mahir AI Default Brand
      </h2>
      <p className="mb-4 text-sm" style={{ color: "var(--color-text-2)" }}>
        Müşteriye özel brand atanmamışsa bu ayarlar kullanılır. Tüm projeler için fallback.
      </p>
      {loading && <div style={{ color: "var(--color-text-3)" }}>Yükleniyor…</div>}
      {error && <div style={{ color: "var(--color-error)" }}>{error}</div>}
      {data && (
        <BrandingEditor
          mode="system"
          initial={data.config}
          initialLogoUrl={data.logoPreviewUrl}
          onSave={handleSave}
          onLogoUpload={handleLogoUpload}
          onLogoDelete={handleLogoDelete}
        />
      )}
    </div>
  );
}

function ClientBrandingPanel(props: { clientId: string; clientName: string }): JSX.Element {
  const { clientId, clientName } = props;
  const [data, setData] = useState<BrandingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await getClientBranding(clientId);
      setData(res);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleSave = async (config: BrandingConfig): Promise<void> => {
    await updateClientBranding(clientId, config);
    await reload();
  };
  const handleDisable = async (): Promise<void> => {
    await disableClientBranding(clientId);
    await reload();
  };
  const handleLogoUpload = async (file: File): Promise<void> => {
    await uploadLogoFlow({ type: "client", clientId }, file);
    await reload();
  };
  const handleLogoDelete = async (): Promise<void> => {
    await deleteClientLogo(clientId);
    await reload();
  };

  return (
    <div>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", marginBottom: 8 }}>
        {clientName}
      </h2>
      <p className="mb-4 text-sm" style={{ color: "var(--color-text-2)" }}>
        Bu müşteri için özel brand. Toggle kapalıysa Mahir Default kullanılır.
        {data?.config
          ? ` · Aktif kaynak: ${data.effective.source}`
          : " · Şu an default kullanılıyor"}
      </p>
      {loading && <div style={{ color: "var(--color-text-3)" }}>Yükleniyor…</div>}
      {error && <div style={{ color: "var(--color-error)" }}>{error}</div>}
      {data && (
        <BrandingEditor
          mode="client"
          initial={data.config}
          initialLogoUrl={data.logoPreviewUrl}
          onSave={handleSave}
          onDisable={handleDisable}
          onLogoUpload={handleLogoUpload}
          onLogoDelete={handleLogoDelete}
        />
      )}
    </div>
  );
}
